import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Radio from '@mui/material/Radio';
import Dialog from '@mui/material/Dialog';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useTranslate } from 'src/locales';
import { PresentationKind } from 'src/services/presentations/presentations.types';
import { useGetCandidateApplicationsQuery } from 'src/services/candidates/candidates.service';
import { useCreatePresentationMutation, useGetPresentationTemplatesQuery } from 'src/services/presentations/presentations.service';

import { withReason } from '../hooks/api-error-reason';

// ----------------------------------------------------------------------

const KINDS: PresentationKind[] = [
  PresentationKind.COVER_PLUS_RESUME,
  PresentationKind.FORMATTED_RESUME,
  PresentationKind.PROFILE,
  PresentationKind.EXECUTIVE,
];

interface Props {
  open: boolean;
  onClose: VoidFunction;
  /** The OrganizationCandidate id. */
  candidateId: string;
  candidateName?: string;
  /** Preselects this application, when the dialog is opened from a job. */
  applicationId?: string;
}

/**
 * "Create presentation" (JT1-1886): pick a type and template, optionally a job, then open the
 * builder on a copy of the candidate's profile.
 */
export default function CreatePresentationDialog({ open, onClose, candidateId, candidateName, applicationId }: Props) {
  const { t } = useTranslate();
  const router = useRouter();

  const { currentData: templates } = useGetPresentationTemplatesQuery(undefined, { skip: !open });
  const { currentData: applications } = useGetCandidateApplicationsQuery({ candidateId, page: 1, per_page: 50 }, { skip: !open });
  const [create, { isLoading }] = useCreatePresentationMutation();

  const defaultTemplate = templates?.find((template) => template.is_default);
  const [kind, setKind] = useState<PresentationKind>(PresentationKind.FORMATTED_RESUME);
  const [templateKey, setTemplateKey] = useState<string>('');
  const [application, setApplication] = useState<string>(applicationId ?? '');
  const [title, setTitle] = useState('');
  const [anonymise, setAnonymise] = useState(false);
  // Once the recruiter picks a template themselves, choosing a job no longer changes it.
  const [templateChosen, setTemplateChosen] = useState(false);

  // Start on the agency's default template and its type, once templates arrive.
  useEffect(() => {
    if (defaultTemplate && !templateKey) {
      setKind(defaultTemplate.kind);
      setTemplateKey(defaultTemplate.key);
    }
  }, [defaultTemplate, templateKey]);

  const forKind = useMemo(() => (templates ?? []).filter((template) => template.kind === kind), [templates, kind]);
  const selected = templates?.find((template) => template.key === templateKey);

  const chooseKind = (next: PresentationKind) => {
    setTemplateChosen(true);
    setKind(next);
    const first = (templates ?? []).find((template) => template.kind === next && template.is_default)
      ?? (templates ?? []).find((template) => template.kind === next);
    setTemplateKey(first?.key ?? '');
    setAnonymise(!!first?.anonymise_by_default);
  };

  // JT1-1887: a job for a client with its own template starts on that template.
  useEffect(() => {
    if (templateChosen || !templates) return;
    const clientId = applications?.results?.find((item) => item.id === application)?.job?.client_profile?.id;
    const forClient = clientId ? templates.find((template) => template.client_company?.id === clientId) : undefined;
    const next = forClient ?? defaultTemplate;
    if (next && next.key !== templateKey) {
      setKind(next.kind);
      setTemplateKey(next.key);
      setAnonymise(!!next.anonymise_by_default);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application, applications, templates, templateChosen]);

  const chooseTemplate = (key: string) => {
    setTemplateChosen(true);
    setTemplateKey(key);
    setAnonymise(!!templates?.find((template) => template.key === key)?.anonymise_by_default);
  };

  const handleCreate = async () => {
    try {
      const presentation = await create({
        candidateId,
        template_key: templateKey || undefined,
        application_id: application || undefined,
        title: title.trim() || undefined,
        anonymise,
      }).unwrap();
      onClose();
      router.push(paths.dashboard.candidates.presentation(candidateId, presentation.id));
    } catch (error) {
      enqueueSnackbar(withReason(t('presentations.create.failed'), error), { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t('presentations.create.title')}
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {t('presentations.create.subtitle', { name: candidateName || t('presentations.create.this_candidate') })}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>{t('presentations.create.type')}</Typography>
            <Box role="radiogroup" aria-label={t('presentations.create.type')} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
              {KINDS.map((value) => (
                <Box
                  key={value}
                  component="label"
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    p: 1.5,
                    cursor: 'pointer',
                    borderRadius: 1.5,
                    border: (theme) => `${kind === value ? 2 : 1}px solid ${kind === value ? theme.palette.text.primary : theme.palette.divider}`,
                    bgcolor: kind === value ? 'action.hover' : 'transparent',
                  }}
                >
                  <Radio checked={kind === value} onChange={() => chooseKind(value)} value={value} size="small" sx={{ p: 0, mt: 0.25 }} />
                  <Box>
                    <Typography variant="subtitle2">{t(`presentations.kinds.${value}.label`)}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t(`presentations.kinds.${value}.description`)}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              fullWidth
              label={t('presentations.create.template')}
              value={forKind.some((template) => template.key === templateKey) ? templateKey : ''}
              onChange={(event) => chooseTemplate(event.target.value)}
              helperText={selected?.description ?? ' '}
            >
              {forKind.map((template) => (
                <MenuItem key={template.key} value={template.key}>
                  {template.is_default ? t('presentations.create.template_default', { name: template.name }) : template.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              label={t('presentations.create.job')}
              value={application}
              onChange={(event) => setApplication(event.target.value)}
              helperText={t('presentations.create.job_helper')}
            >
              <MenuItem value="">{t('presentations.create.job_none')}</MenuItem>
              {(applications?.results ?? []).map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {[item.job?.title, item.job?.client_profile?.company_name ?? item.job?.client_company_name].filter(Boolean).join(' · ')}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            fullWidth
            label={t('presentations.create.title_label')}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            inputProps={{ maxLength: 200 }}
          />

          <FormControlLabel
            control={<Switch checked={anonymise} onChange={(event) => setAnonymise(event.target.checked)} />}
            label={(
              <Box>
                <Typography variant="subtitle2">{t('presentations.anonymise.toggle')}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('presentations.create.anonymise_helper')}</Typography>
              </Box>
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <LoadingButton variant="contained" loading={isLoading} disabled={!templates} onClick={handleCreate}>
          {t('presentations.create.submit')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
